import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Checkbox } from '@/common/ui/checkbox'
import { DateTimePicker } from '@/common/components/DateTimePicker'
import useQueryPaymentGateProvider from '@/modules/bank/api/hooks/useQueryPaymentGateProvider'
import useMutationCreateMaintenance from '@/modules/bank/api/hooks/useMutationCreateMaintenance'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import showErrorToast from '@/common/utils/showErrorToast'
import { Wrench } from 'lucide-react'
import MaintenanceKeyConfirmDialog from '../MaintenanceKeyConfirmDialog'

const formSchema = z
	.object({
		bankProviderName: z.string().min(1, 'กรุณาเลือกผู้ให้บริการ'),
		depositEnabled: z.boolean(),
		depositStartAt: z.string().optional(),
		depositEndAt: z.string().optional(),
		withdrawEnabled: z.boolean(),
		withdrawStartAt: z.string().optional(),
		withdrawEndAt: z.string().optional(),
	})
	.refine(
		(data) => {
			if (data.depositEnabled) return !!data.depositStartAt
			return true
		},
		{ message: 'กรุณากรอกเวลาเริ่มต้น', path: ['depositStartAt'] },
	)
	.refine(
		(data) => {
			if (data.withdrawEnabled) return !!data.withdrawStartAt
			return true
		},
		{ message: 'กรุณากรอกเวลาเริ่มต้น', path: ['withdrawStartAt'] },
	)

type FormValues = z.infer<typeof formSchema>

interface CreateMaintenanceModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
}

type PendingPayload = {
	bankProviderName: string
	depositMaintenance?: { startAt: string; endAt?: string }
	withdrawMaintenance?: { startAt: string; endAt?: string }
}

export default function CreateMaintenanceModal({
	open,
	onOpenChange,
}: CreateMaintenanceModalProps) {
	const { data: providers, isLoading: isLoadingProviders } =
		useQueryPaymentGateProvider()
	const { mutateAsync, isPending } = useMutationCreateMaintenance()
	const [confirmOpen, setConfirmOpen] = useState(false)
	const [pendingPayload, setPendingPayload] = useState<PendingPayload | null>(
		null,
	)

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			bankProviderName: '',
			depositEnabled: false,
			depositStartAt: '',
			depositEndAt: '',
			withdrawEnabled: false,
			withdrawStartAt: '',
			withdrawEndAt: '',
		},
	})

	const handleSubmit = (values: FormValues) => {
		const payload: PendingPayload = {
			bankProviderName: values.bankProviderName,
		}
		if (values.depositEnabled && values.depositStartAt) {
			payload.depositMaintenance = {
				startAt: new Date(values.depositStartAt).toISOString(),
				endAt: values.depositEndAt
					? new Date(values.depositEndAt).toISOString()
					: undefined,
			}
		}
		if (values.withdrawEnabled && values.withdrawStartAt) {
			payload.withdrawMaintenance = {
				startAt: new Date(values.withdrawStartAt).toISOString(),
				endAt: values.withdrawEndAt
					? new Date(values.withdrawEndAt).toISOString()
					: undefined,
			}
		}
		if (!payload.depositMaintenance && !payload.withdrawMaintenance) {
			showErrorToast(new Error('กรุณาเลือกอย่างน้อยหนึ่งประเภท: ฝากหรือถอน'))
			return
		}
		setPendingPayload(payload)
		setConfirmOpen(true)
	}

	const handleConfirmWithKey = async (maintenanceKey: string) => {
		if (!pendingPayload) return
		try {
			await mutateAsync({ payload: pendingPayload, maintenanceKey })
			setPendingPayload(null)
			setConfirmOpen(false)
			form.reset()
			onOpenChange(false)
		} catch (err) {
			showErrorToast(err as Error)
		}
	}

	const depositEnabled = form.watch('depositEnabled')
	const withdrawEnabled = form.watch('withdrawEnabled')

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<Wrench className="h-5 w-5" />
							สร้างการซ่อมบำรุง
						</DialogTitle>
					</DialogHeader>
					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(handleSubmit)}
							className="space-y-6"
						>
							<FormField
								control={form.control}
								name="bankProviderName"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ผู้ให้บริการ</FormLabel>
										<Select
											onValueChange={field.onChange}
											value={field.value}
											disabled={isLoadingProviders}
										>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="เลือกผู้ให้บริการ" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{providers?.map((p) => (
													<SelectItem key={p.id} value={p.name}>
														{p.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="space-y-4">
								<FormField
									control={form.control}
									name="depositEnabled"
									render={({ field }) => (
										<FormItem className="flex flex-row items-start space-x-3 space-y-0">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											<div className="space-y-1 leading-none">
												<FormLabel>ฝากเงิน ปิดปรับปรุง</FormLabel>
											</div>
										</FormItem>
									)}
								/>
								{depositEnabled && (
									<div className="ml-6 space-y-2 border-l-2 border-muted pl-4">
										<FormField
											control={form.control}
											name="depositStartAt"
											render={({ field }) => {
												const dateValue = field.value
													? new Date(field.value)
													: undefined
												return (
													<FormItem>
														<FormLabel>เวลาเริ่มต้น</FormLabel>
														<FormControl>
															<DateTimePicker
																date={dateValue}
																onDateSelect={(date) =>
																	field.onChange(date ? date.toISOString() : '')
																}
																placeholder="เลือกวันและเวลาเริ่มต้น"
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)
											}}
										/>
										<FormField
											control={form.control}
											name="depositEndAt"
											render={({ field }) => {
												const dateValue = field.value
													? new Date(field.value)
													: undefined
												return (
													<FormItem>
														<FormLabel>
															เวลาสิ้นสุด (ไม่กรอก = จนกว่าจะยกเลิก)
														</FormLabel>
														<FormControl>
															<DateTimePicker
																date={dateValue}
																onDateSelect={(date) =>
																	field.onChange(date ? date.toISOString() : '')
																}
																placeholder="เลือกวันและเวลาสิ้นสุด (ไม่บังคับ)"
															/>
														</FormControl>
														<FormDescription>
															เว้นว่างได้ = ซ่อมบำรุงจนกว่าผู้ดูแลจะยกเลิก
														</FormDescription>
														<FormMessage />
													</FormItem>
												)
											}}
										/>
									</div>
								)}
							</div>

							<div className="space-y-4">
								<FormField
									control={form.control}
									name="withdrawEnabled"
									render={({ field }) => (
										<FormItem className="flex flex-row items-start space-x-3 space-y-0">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											<div className="space-y-1 leading-none">
												<FormLabel>ถอนเงิน ปิดปรับปรุง</FormLabel>
											</div>
										</FormItem>
									)}
								/>
								{withdrawEnabled && (
									<div className="ml-6 space-y-2 border-l-2 border-muted pl-4">
										<FormField
											control={form.control}
											name="withdrawStartAt"
											render={({ field }) => {
												const dateValue = field.value
													? new Date(field.value)
													: undefined
												return (
													<FormItem>
														<FormLabel>เวลาเริ่มต้น</FormLabel>
														<FormControl>
															<DateTimePicker
																date={dateValue}
																onDateSelect={(date) =>
																	field.onChange(date ? date.toISOString() : '')
																}
																placeholder="เลือกวันและเวลาเริ่มต้น"
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)
											}}
										/>
										<FormField
											control={form.control}
											name="withdrawEndAt"
											render={({ field }) => {
												const dateValue = field.value
													? new Date(field.value)
													: undefined
												return (
													<FormItem>
														<FormLabel>
															เวลาสิ้นสุด (ไม่กรอก = จนกว่าจะยกเลิก)
														</FormLabel>
														<FormControl>
															<DateTimePicker
																date={dateValue}
																onDateSelect={(date) =>
																	field.onChange(date ? date.toISOString() : '')
																}
																placeholder="เลือกวันและเวลาสิ้นสุด (ไม่บังคับ)"
															/>
														</FormControl>
														<FormDescription>
															เว้นว่างได้ = ซ่อมบำรุงจนกว่าผู้ดูแลจะยกเลิก
														</FormDescription>
														<FormMessage />
													</FormItem>
												)
											}}
										/>
									</div>
								)}
							</div>

							<div className="flex justify-end gap-2">
								<Button
									type="button"
									variant="outline"
									onClick={() => onOpenChange(false)}
								>
									ยกเลิก
								</Button>
								<Button type="submit" disabled={isPending}>
									{isPending ? 'กำลังสร้าง...' : 'สร้างการซ่อมบำรุง'}
								</Button>
							</div>
						</form>
					</Form>
				</DialogContent>
			</Dialog>
			<MaintenanceKeyConfirmDialog
				open={confirmOpen}
				onOpenChange={setConfirmOpen}
				title="ยืนยันการสร้างการซ่อมบำรุง"
				description="กรอกรหัสยืนยัน (BANK_MAINTENANCE_KEY) เพื่อดำเนินการ"
				confirmLabel="ยืนยัน"
				onConfirm={handleConfirmWithKey}
				isPending={isPending}
			/>
		</>
	)
}
